import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePusher } from '../../../contexts/PusherContext';
import { Agent, AgentEvents, ManagerChangeEvent } from '@dreamai/types';

interface AgentOption {
  value: string;
  label: string;
  dreamieImageUrl: string | undefined;
  predominantColour: string | undefined;
}

interface ManagerChangeListenerProps {
  agents: Agent[];
  setAgents: React.Dispatch<React.SetStateAction<Agent[]>>;
  selectedAgent: AgentOption | null;
  setSelectedAgent: React.Dispatch<React.SetStateAction<AgentOption | null>>;
  fetchAgents: () => Promise<void>;
}

/**
 * Component that listens for manager hired and fired events and updates the agent list accordingly
 * This is a "headless" component that doesn't render any UI elements
 */
const ManagerChangeListener: FC<ManagerChangeListenerProps> = ({
  agents,
  setAgents,
  selectedAgent,
  setSelectedAgent,
  fetchAgents,
}) => {
  const pusherContext = usePusher(true); // Get full context to access both channels
  const userChannel = pusherContext?.userChannel;
  const companyChannel = pusherContext?.pusherChannel;
  const navigate = useNavigate();

  // Listen for manager hired and fired events from Pusher
  // Listen on BOTH user channel (for user-owned agents) and company channel (for company-owned agents)
  useEffect(() => {
    if (!userChannel || !companyChannel) return;

    // Handle manager fired event
    const handleManagerFired = (data: ManagerChangeEvent) => {
      console.log('Received manager fired event:', data);

      // Remove the agent from the agents list
      setAgents((prevAgents) => {
        return prevAgents.filter((agent) => agent.id !== data.agentId);
      });

      // If this is the currently selected agent, navigate back to the main page
      if (selectedAgent && selectedAgent.value === data.agentId) {
        // Find a default agent to select (Business Manager or first available)
        const remainingAgents = agents.filter((a) => a.id !== data.agentId);
        if (remainingAgents.length > 0) {
          const businessManager = remainingAgents.find(
            (a) => a.agent_type === 'Business Manager',
          );
          const defaultAgent = businessManager || remainingAgents[0];

          // Navigate to the main page with the default agent
          navigate(`/?agent_id=${defaultAgent.id}`);
        } else {
          // If no agents left, just go to the main page
          navigate('/');
        }
      }
    };

    // Handle manager hired event
    const handleManagerHired = (data: ManagerChangeEvent) => {
      console.log('Received manager hired event:', data);

      // Refresh the agents list to include the new agent
      fetchAgents();
    };

    // Handle manager image updated event
    const handleManagerImageUpdated = (data: ManagerChangeEvent) => {
      console.log('Received manager image updated event:', data);

      // Refresh the agents list to update agent details
      fetchAgents();
    };

    // Bind event listeners to both channels
    userChannel.bind(AgentEvents.MANAGER_FIRED, handleManagerFired);
    userChannel.bind(AgentEvents.MANAGER_HIRED, handleManagerHired);
    userChannel.bind(
      AgentEvents.MANAGER_IMAGE_UPDATED,
      handleManagerImageUpdated,
    );

    companyChannel.bind(AgentEvents.MANAGER_FIRED, handleManagerFired);
    companyChannel.bind(AgentEvents.MANAGER_HIRED, handleManagerHired);
    companyChannel.bind(
      AgentEvents.MANAGER_IMAGE_UPDATED,
      handleManagerImageUpdated,
    );

    // Cleanup function to unbind the event listeners when component unmounts
    return () => {
      userChannel.unbind(AgentEvents.MANAGER_FIRED, handleManagerFired);
      userChannel.unbind(AgentEvents.MANAGER_HIRED, handleManagerHired);
      userChannel.unbind(
        AgentEvents.MANAGER_IMAGE_UPDATED,
        handleManagerImageUpdated,
      );

      companyChannel.unbind(AgentEvents.MANAGER_FIRED, handleManagerFired);
      companyChannel.unbind(AgentEvents.MANAGER_HIRED, handleManagerHired);
      companyChannel.unbind(
        AgentEvents.MANAGER_IMAGE_UPDATED,
        handleManagerImageUpdated,
      );
    };
  }, [
    userChannel,
    companyChannel,
    agents,
    selectedAgent,
    setAgents,
    navigate,
    fetchAgents,
  ]);

  // This component doesn't render anything
  return null;
};

export default ManagerChangeListener;
